/*
 * The product's wordmark: "teamBase" set in Nunito 800 and converted to two paths once, so no 800
 * face ships and the mark is never live text — it looks the same before the interface font has
 * arrived, at every size, on every machine.
 *
 * The picture lives in `/icons/wordmark.svg` and is pulled in with `<use>` rather than inlined,
 * because the same six kilobytes are then fetched once and cached for every surface that shows the
 * mark. An externally referenced `use` builds its shadow tree from the *referenced* document: a
 * selector written here never reaches those two paths, but an inherited custom property crosses,
 * which is why the second half of the word reads `var(--tb-wordmark-base)` inside the file itself
 * and `wordmark.css` does nothing but set that property per variant (measured in Chromium under the
 * product's own content-security policy).
 *
 * The mark is decorative, like every `Icon`: the surface around it carries the name — a heading, a
 * visually hidden label, the document title — so a reader with a screen reader hears the product
 * named once rather than twice.
 */

import type { ReactNode } from "react"
import "./wordmark.css"

/** The shipped asset's own box; the two agree, so a mark asked for 112 px is 112 px of ink wide. */
const VIEW_BOX = "-1 0 465 100"
const RATIO = 465 / 100

/**
 * `variant` is which colour the "Base" half takes: `brand` is the lime on the navy rail, `accent`
 * the link blue on a page, `mono` the surrounding text colour. `width` is the drawn width in CSS
 * pixels — the height follows the mark's own proportions.
 */
export function Wordmark({
	variant,
	width,
	className,
}: {
	variant: "brand" | "accent" | "mono"
	width: number
	className?: string
}): ReactNode {
	return (
		<svg
			className={`tb-wordmark tb-wordmark--${variant}${className === undefined ? "" : ` ${className}`}`}
			viewBox={VIEW_BOX}
			width={width}
			height={Math.round((width / RATIO) * 100) / 100}
			aria-hidden="true"
			focusable="false"
		>
			<use href="/icons/wordmark.svg#wordmark" />
		</svg>
	)
}
